import ChartStyles from '@/utils/chart_styles'
import Utilities from '@/utils/utilities'
import { Controller } from '@hotwired/stimulus'
import Chart from 'chart.js/auto'
import 'chartjs-adapter-luxon'
import { DateTime } from 'luxon'

const MIN_CONTAINER_WIDTH = 150

export default class extends Controller {
  static targets = ['chart']

  connect() {
    console.log('Sparkline controller connected')
    this.element[this.identifier] = this
    this.metricType = this.data.get('metricValue')

    if (this.metricType) {
      this.initializeSparkline()
      this.setupResizeListener()
    } else {
      console.error('No metric type found in data attributes')
    }
  }

  initializeSparkline() {
    if (this.element.offsetWidth >= MIN_CONTAINER_WIDTH) {
      this.requestChartData()
    } else {
      this.element.style.display = 'none'
    }
  }

  setupResizeListener() {
    this.handleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.handleResize)
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
    window.removeEventListener('resize', this.handleResize)
  }

  requestChartData() {
    const path = `/business-pulse/key-metrics-sparkline/${this.metricType}`

    fetch(path, {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => response.json())
      .then((data) => {
        this.chartData = data
        this.buildChart(data)
      })
      .catch((error) => {
        console.error('Error loading sparkline data:', error)
      })
  }

  buildChart(data) {
    if (!data.points || data.points.length === 0) {
      return
    }

    // Process data points

    const chartData = {
      data: data.points.map((point) => ({
        x: DateTime.fromISO(point.x).setZone(this.timezoneOffset),
        y: point.y
      }))
    }

    const plotOptions = {
      backgroundColor: Utilities.opacitize(ChartStyles.colors.blueGray, 0.08),
      borderColor: ChartStyles.colors.blueGray,
      borderWidth: 3,
      pointRadius: 2,
      pointHoverRadius: 5,
      pointBorderWidth: 0,
      pointHoverBorderWidth: 2,
      pointBackgroundColor: ChartStyles.colors.blueGray,
      pointHoverBackgroundColor: '#ffffff',
      pointHoverBorderColor: ChartStyles.colors.blueGray,
      fill: 'origin',
      tension: ChartStyles.tension
    }

    const finalChartData = Object.assign({}, chartData, plotOptions)

    const plotData = {
      datasets: [finalChartData]
    }

    this.chart = new Chart(this.chartTarget, {
      type: 'line',
      data: plotData,
      options: {
        maintainAspectRatio: false,
        responsive: true,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        animation: {
          duration: 300
        },
        scales: {
          x: {
            display: true,
            type: 'time',
            time: {
              unit: 'month',
              displayFormats: {
                month: 'MMM'
              },
              tooltipFormat: 'MMM yyyy'
            },
            adapters: {
              date: {
                zone: this.timezoneOffset
              }
            },
            ticks: {
              source: 'data'
            }
          },
          y: {
            display: false,
            beginAtZero: false
          }
        },
        plugins: {
          annotation: {},
          crosshair: {
            snap: {
              enabled: true
            },
            line: {
              color: ChartStyles.colors.blueGrayLight,
              width: 1,
              dashPattern: [4],
              drawUnderChart: false
            },
            zoom: {
              enabled: false
            }
          },
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            mode: 'index',
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            borderColor: ChartStyles.colors.blueGray,
            borderWidth: 1,
            cornerRadius: 6,
            displayColors: false,
            callbacks: {
              title: function (context) {
                const date = new Date(context[0].parsed.x)
                return date.toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric'
                })
              },
              label: function (context) {
                const value = context.parsed.y
                return new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  notation: 'compact',
                  maximumFractionDigits: 1
                }).format(value)
              }
            }
          }
        },
        elements: {
          point: {
            hoverRadius: 4
          }
        },
        layout: {
          padding: {
            top: 5,
            bottom: 5,
            left: 5,
            right: 5
          }
        }
      }
    })
  }

  handleResize() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
    this.resizeTimeout = setTimeout(() => {
      let containerWidth
      const isHidden = this.element.style.display === 'none'

      if (isHidden) {
        this.element.style.display = ''
        containerWidth = this.element.offsetWidth
      } else {
        containerWidth = this.element.offsetWidth
      }

      if (containerWidth < MIN_CONTAINER_WIDTH) {
        this.element.style.display = 'none'
        if (this.chart) {
          this.chart.destroy()
          this.chart = null
        }
      } else {
        // Keep element visible
        this.element.style.display = ''
        if (!this.chart) {
          // If we don't have a chart but have data, rebuild it
          if (this.chartData) {
            this.buildChart(this.chartData)
          } else {
            // If we don't have data, request it
            this.requestChartData()
          }
        } else {
          // If we have a chart, just resize it
          this.chart.resize()
        }
      }
    }, 250)
  }
}
